import React, { useState } from 'react';
import classes from './CodeField.module.scss';
import { Stack } from 'new/elements/Stack';
import Typography from 'new/elements/Typography';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import { ModalDefault } from 'new/routes/ModalDefault';
import { Button } from 'new/elements/Button';
import { useDebouncedCallback } from 'new/hooks/useDebouncedCallback';
import { darcula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';

interface CodeFieldProps {
  onChange: (value: string) => void;
  label: string;
  value: string;
  description?: string;
  language?: string;
  modalTitle?: string;
}

export const CodeField = (props: CodeFieldProps) => {
  const {
    onChange,
    label = 'Create your code',
    value,
    description,
    language = 'javascript',
    modalTitle = 'Create Code',
  } = props;
  const [showModal, setShowModal] = useState(false);
  const [code, setCode] = useState('');

  const onCloseModal = () => {
    setShowModal(false);
  };

  const onShowModal = () => {
    setShowModal(true);
  };

  const onChangeCode = useDebouncedCallback(
    (newCode) => {
      setCode(newCode);
    },
    [],
    500,
  );

  return (
    <Stack marginTop="8px" className={classes.container}>
      <Stack alignItems={'flex-start'}>
        <Button
          data-testid={props['data-testid']}
          size="small"
          variant="outlined"
          onClick={onShowModal}
        >
          {label}
        </Button>
      </Stack>
      {!!value ? (
        <SyntaxHighlighter onClick={onShowModal} language={language} style={darcula}>
          {value}
        </SyntaxHighlighter>
      ) : null}
      <ModalDefault
        data-testid={`${props['data-testid']}-modal`}
        open={showModal}
        title={modalTitle}
        fullWidth
        actions={{
          ok: {
            text: 'Save',
            onClick: () => {
              setShowModal(false);
              onChange(code);
            },
          },
        }}
        onCancel={onCloseModal}
      >
        <Stack>
          <Typography variant="bodySmall">{`${language.toUpperCase()} FUNCTION`}</Typography>
          <Stack borderRadius="8px" overflow="hidden" minHeight={500} width="100%">
            <LoadableMonacoEditor
              language={language}
              theme="dark"
              width="100%"
              value={value}
              height={500}
              onChange={onChangeCode}
              options={{
                minimap: { enabled: false },
              }}
            />
          </Stack>
          <Typography variant="bodySmall">{description}</Typography>
        </Stack>
      </ModalDefault>
    </Stack>
  );
};
